import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';

import { KsOffer, KsOfferFigures } from '../../../../core/kickstart/kickstart.types';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';

/**
 * The first-debit sentence, chosen by what the offer RECORD says the date is
 * (DOC-62-B §3). Wording provisional (DOC-62 §9). A record that does not say has
 * no sentence — the page never guesses which kind a date is.
 */
export const FIRST_DEBIT_SENTENCES: Record<KsOfferFigures['first_debit_kind'], string> = {
  CALCULATED:
    'This assumes we pay out today. If payout is later, your first debit moves to the ' +
    'first debit date at least seven days after payout. Your instalment, number of ' +
    'instalments and total stay the same.',
  OPS_SET:
    'This date stays if payout is later — unless payout is fewer than seven days before ' +
    'it. Then your first debit moves to the next debit date at least seven days after ' +
    'payout. Your instalment, number of instalments and total stay the same.',
};

/** The figures a changed offer marks, old beside new (DOC-62 §5.2). */
type Line = 'amount' | 'fee' | 'total' | 'instalment' | 'term' | 'first_debit';

/**
 * The Kickstart offer card — Screen 5.0's seven figures (DOC-62 §4.1).
 *
 * 🚨 **Presentational, and it computes NONE of the figures.** Every value is the
 * offer record's, formatted and nothing more: a screen that works a figure out can
 * disagree with the agreement, and the agreement must match the last offer
 * accepted. No "up to", no range, no estimate, no frequency selector.
 *
 * Reused read-only wherever the offer is shown — the accepted state, Screen 5.1's
 * summary and the Ops panel (Ops sees the offer exactly as the client will).
 */
@Component({
  selector: 'app-ks-offer-card',
  imports: [DatePipe, ZarPipe],
  templateUrl: './ks-offer-card.html',
  styleUrl: './ks-offer-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsOfferCardComponent {
  readonly offer = input.required<KsOffer>();

  protected readonly frequencyWord = computed(() =>
    this.offer().repayment_frequency === 'WEEKLY' ? 'weekly' : 'monthly',
  );

  protected readonly firstDebitSentence = computed(
    () => FIRST_DEBIT_SENTENCES[this.offer().first_debit_kind] ?? null,
  );

  /** Lines whose value differs from the version this one replaced. */
  protected readonly changed = computed<Set<Line>>(() => {
    const now = this.offer();
    const before = now.previous;
    const out = new Set<Line>();
    if (!before) return out;
    if (before.approved_amount !== now.approved_amount) out.add('amount');
    if (before.facility_fee_amount !== now.facility_fee_amount) out.add('fee');
    if (before.total_repayable !== now.total_repayable) out.add('total');
    if (
      before.instalment_amount !== now.instalment_amount ||
      before.repayment_frequency !== now.repayment_frequency
    ) {
      out.add('instalment');
    }
    if (before.instalment_count !== now.instalment_count) out.add('term');
    if (before.first_debit_date !== now.first_debit_date) out.add('first_debit');
    return out;
  });

  protected wordFor(frequency: KsOfferFigures['repayment_frequency']): string {
    return frequency === 'WEEKLY' ? 'weekly' : 'monthly';
  }
}
